// Block-ID stamping for the screenplay editor.
//
// Every block-level node carries a `bid` attribute (UUIDv7) so the
// future sync layer can address blocks by stable identity rather than
// by position. This module owns two surfaces:
//
//   1. `stampDoc(doc)` — a pure helper that walks a freshly built doc
//      and returns one whose blocks all have a bid. Used by the
//      Fountain importer and the editor's mount path so docs that
//      arrive from disk are stamped before the EditorView ever sees
//      them.
//
//   2. `blockIdPlugin()` — an `appendTransaction` plugin that runs
//      after any docChanged transaction and stamps any block lacking
//      a bid. This is the runtime safety net: splits, pastes, input
//      rules, and any future command that builds a fresh node without
//      pre-populating attrs all get their IDs assigned here.
//
// Why both? `stampDoc` covers the initial doc (appendTransaction
// doesn't fire on load), and `blockIdPlugin` covers everything else.
// The two together make "every block has a bid" an invariant the rest
// of the codebase can rely on.

import { Plugin } from 'prosemirror-state';
import { uuidv7, isUuid } from './id.js';

const BLOCK_ID_META = 'lastslate-block-ids';

// Schema feature-detection: only block-level nodes (`group: 'block'`)
// carry a `bid` attr in our schema. Cheaper than pattern-matching node
// names, and stays correct if we add more block types later.
function isStampableBlock(node) {
  if (!node || !node.type) return false;
  const spec = node.type.spec;
  if (!spec || spec.group !== 'block') return false;
  return Boolean(spec.attrs && 'bid' in spec.attrs);
}

function needsStamp(node) {
  if (!isStampableBlock(node)) return false;
  return !isUuid(node.attrs && node.attrs.bid);
}

// Pure helper: returns a doc identical to `doc` except every block
// missing a `bid` has one assigned. Our schema is flat (doc > block+),
// so a single-level walk suffices.
export function stampDoc(doc) {
  if (!doc || !doc.type || doc.type.name !== 'doc') return doc;
  let changed = false;
  const children = [];
  doc.forEach((node) => {
    if (needsStamp(node)) {
      changed = true;
      children.push(node.type.create({ ...node.attrs, bid: uuidv7() }, node.content, node.marks));
    } else {
      children.push(node);
    }
  });
  if (!changed) return doc;
  return doc.type.create(doc.attrs, children);
}

// Runtime safety net: any docChanged transaction whose result contains
// a block without a bid gets an appended transaction stamping the
// missing ones. Tagged with BLOCK_ID_META so we don't loop on our own
// output (and so the inference plugin can ignore us when it cares).
export function blockIdPlugin() {
  return new Plugin({
    appendTransaction(transactions, _oldState, newState) {
      if (!transactions.some((t) => t.docChanged)) return null;
      if (transactions.some((t) => t.getMeta(BLOCK_ID_META))) return null;

      const stamps = [];
      newState.doc.descendants((node, pos) => {
        if (needsStamp(node)) stamps.push({ pos, attrs: node.attrs });
        return true;
      });
      if (stamps.length === 0) return null;

      const tr = newState.tr;
      for (const { pos, attrs } of stamps) {
        tr.setNodeMarkup(pos, null, { ...attrs, bid: uuidv7() });
      }
      tr.setMeta(BLOCK_ID_META, true);
      // No selection change, no scroll — silent invariant repair.
      return tr;
    },
  });
}

export const __INTERNAL__ = { isStampableBlock, needsStamp, BLOCK_ID_META };
